import {Controller} from "@hotwired/stimulus";
import Debug from 'debug';

export const name = 'redirect';

const debug = Debug(`app:${name}`);

// Define the shape of the Stimulus Values
interface CountdownValues {
    timeout: number;
    url: string;
}

// Define the Stimulus component properties and targets
export default class extends Controller<HTMLElement> {
    // 1. Define Targets: The element that will display the countdown value.
    static targets = ["display"]
    readonly displayTarget!: HTMLElement

    // 2. Define Values and their Types:
    static values = {
        timeout: { type: Number, default: 10 },
        url: { type: String, default: "/" }
    }

    // TypeScript properties based on static values (Read-only)
    readonly timeoutValue!: number
    readonly urlValue!: string

    // Internal state variable
    private currentTimeout: number = 0;
    private timer: number | undefined;

    connect(): void {
        // Initialize the internal state from the configured value
        this.currentTimeout = this.timeoutValue;

        this.updateDisplay();
        this.startCountdown();
    }

    disconnect(): void {
        // Crucial: Clear the interval when the controller is removed from the DOM
        this.stopCountdown();
    }

    /**
     * Starts the 1-second interval for the countdown.
     */
    startCountdown(): void {
        // Use window.setInterval which returns a numeric ID in browsers
        this.timer = window.setInterval(() => {
            this.currentTimeout--;
            this.updateDisplay();

            if (this.currentTimeout <= 0) {
                this.stopCountdown();
                this.redirectToUrl();
            }
        }, 1000); // 1000 millitimeout = 1 second
    }

    /**
     * Clears the interval timer.
     */
    stopCountdown(): void {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
    }

    /**
     * Updates the content of the target element.
     */
    updateDisplay(): void {
        // Ensure the target element is available before trying to update its content
        this.displayTarget.textContent = String(Math.max(0, this.currentTimeout));
    }

    /**
     * Redirects the user to the configured URL.
     */
    redirectToUrl(): void {
        debug(`Countdown finished. Redirecting to: ${this.urlValue}`);
        window.location.href = this.urlValue;
    }
}